import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { PawPrint, ArrowRight, ArrowLeft, Loader2, CheckCircle2, Plus, Sparkles, Heart, Mail, Lock, User, X } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { DogPicker } from '../components/DogPicker';
import { BREEDS, AGE_OPTIONS } from '../constants/dogOptions';
import { useBox } from '../context/BoxContext';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ACTIVITY_OPTIONS = [
  { value: 'low', label: 'Low', desc: 'Short walks, mostly resting' },
  { value: 'moderate', label: 'Moderate', desc: 'Daily walks, some play' },
  { value: 'high', label: 'High', desc: 'Runs, agility, working dog' },
];

export const DogMatch = ({ onCompleteOrder }) => {
  const { addProduct, isProductSelected, isFull, selectedProducts, boxSize } = useBox();
  const { customer, login, register } = useAuth();

  const [step, setStep] = useState('form'); // form, matching, results
  const [matching, setMatching] = useState(false);
  const [matches, setMatches] = useState(null);
  const [summary, setSummary] = useState('');
  const [showAuthPopup, setShowAuthPopup] = useState(false);
  const [showBoxFullPopup, setShowBoxFullPopup] = useState(false);
  const [authMode, setAuthMode] = useState('login');
  const [authEmail, setAuthEmail] = useState('');
  const [authPassword, setAuthPassword] = useState('');
  const [authName, setAuthName] = useState('');
  const [authError, setAuthError] = useState('');
  const [authSubmitting, setAuthSubmitting] = useState(false);

  // Form fields
  const [dogName, setDogName] = useState('');
  const [breed, setBreed] = useState('');
  const [age, setAge] = useState('');
  const [weight, setWeight] = useState('');
  const [activityLevel, setActivityLevel] = useState('');
  const [neutered, setNeutered] = useState(null);
  const [sensitivities, setSensitivities] = useState('');
  const [currentFood, setCurrentFood] = useState('');
  const [preferences, setPreferences] = useState('');

  const canSubmit = dogName.trim() && breed && age;

  const handleFindClick = () => {
    if (!canSubmit) return;
    if (customer) {
      handleSubmit();
    } else {
      setShowAuthPopup(true);
    }
  };

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setMatching(true);
    setStep('matching');
    setShowAuthPopup(false);

    const token = localStorage.getItem('customer_token');
    try {
      const response = await axios.post(`${API}/dog-match`, {
        dog_name: dogName.trim(),
        breed,
        age,
        weight: weight || null,
        activity_level: activityLevel || null,
        neutered,
        sensitivities: sensitivities || null,
        current_food: currentFood || null,
        preferences: preferences || null,
      }, { headers: token ? { Authorization: `Bearer ${token}` } : {} });

      if (response.data.status === 'success') {
        setMatches(response.data.matches);
        setSummary(response.data.summary);
        setStep('results');
      } else {
        toast.error(response.data.message || 'Could not find matches');
        setStep('form');
      }
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Matching failed. Please try again.');
      setStep('form');
    } finally {
      setMatching(false);
    }
  };

  const handleReset = () => {
    setStep('form');
    setMatches(null);
    setSummary('');
  };

  const handleAddToBox = (product) => {
    if (!isFull && !isProductSelected(product.id)) {
      addProduct(product);
      toast.success(`${product.name} added to your box!`);
      if (selectedProducts.length + 1 >= boxSize) {
        setTimeout(() => setShowBoxFullPopup(true), 500);
      }
    }
  };

  const handleAuthSubmit = async (e) => {
    e.preventDefault();
    setAuthError('');
    setAuthSubmitting(true);
    try {
      if (authMode === 'register') {
        if (!authName.trim()) { setAuthError('Please enter your name'); setAuthSubmitting(false); return; }
        await register(authName.trim(), authEmail.trim(), authPassword);
      } else {
        await login(authEmail.trim(), authPassword);
      }
      toast.success(authMode === 'register' ? 'Account created!' : 'Signed in!');
      handleSubmit();
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (typeof detail === 'string') setAuthError(detail);
      else if (Array.isArray(detail)) setAuthError(detail.map(e => e?.msg).join('. '));
      else setAuthError('Something went wrong. Please try again.');
    } finally {
      setAuthSubmitting(false);
    }
  };

  const handleGoogleLogin = () => {
    const redirectUrl = window.location.origin + '/';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <AnimatePresence mode="wait">
        {/* STEP 1: Form */}
        {step === 'form' && (
          <motion.div
            key="form"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
          >
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="relative overflow-hidden rounded-none sm:rounded-3xl mb-8 -mx-4 sm:mx-0 px-6 sm:px-10 py-10 sm:py-12 text-center"
              style={{
                backgroundColor: '#1f6d62',
                backgroundImage: `
                  radial-gradient(circle at 20% 15%, rgba(160, 218, 57, 0.12) 0%, transparent 45%),
                  radial-gradient(circle at 85% 80%, rgba(160, 218, 57, 0.10) 0%, transparent 50%),
                  url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='160' height='160'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2' seed='7'/><feColorMatrix values='0 0 0 0 1  0 0 0 0 1  0 0 0 0 1  0 0 0 0.08 0'/></filter><rect width='100%25' height='100%25' filter='url(%23n)'/></svg>")
                `,
              }}
              data-testid="dog-match-hero"
            >
              <svg className="absolute -top-8 -left-10 w-60 h-20 opacity-20 pointer-events-none" viewBox="0 0 300 80" fill="none">
                <path d="M0 40 Q 75 10, 150 40 T 300 40" stroke="#a0da39" strokeWidth="3" strokeLinecap="round" />
              </svg>
              <svg className="absolute bottom-4 right-4 w-60 h-20 opacity-15 pointer-events-none" viewBox="0 0 300 80" fill="none">
                <path d="M0 40 Q 75 70, 150 40 T 300 40" stroke="#a0da39" strokeWidth="3" strokeLinecap="round" />
              </svg>

              <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[11px] font-black uppercase tracking-widest text-[#1f6d62] bg-[#a0da39] mb-4">
                <Sparkles className="w-3 h-3" /> Personalised Match
              </span>
              <div className="w-16 h-16 rounded-2xl bg-white/10 border border-white/20 backdrop-blur-sm flex items-center justify-center mx-auto mb-4">
                <PawPrint className="w-8 h-8 text-[#a0da39]" />
              </div>
              <h2 className="text-white text-3xl md:text-5xl font-extrabold uppercase tracking-tight leading-[0.95]">
                Get <span className="text-[#a0da39]">{dogName || 'Your Dog'}</span> Matched
              </h2>
              <p
                className="text-[#a0da39] mt-3 text-xl md:text-2xl italic"
                style={{ fontFamily: "'Caveat', 'Dancing Script', cursive" }}
              >
                A personalised plan in minutes.
              </p>
              <p className="text-white/85 mt-3 text-sm md:text-base max-w-md mx-auto leading-relaxed">
                Tell us about your dog and Ted AI will recommend the perfect food from our range.
              </p>
            </motion.div>

            <div className="bg-white rounded-2xl border border-gray-200 p-6 space-y-5" data-testid="dog-match-form">
              {/* Dog Name */}
              <DogPicker
                value={dogName}
                onChange={setDogName}
                label="Dog's Name"
                required
                placeholder="e.g. Buddy"
                testId="dog-name"
              />

              {/* Breed */}
              <div>
                <label className="block text-sm font-semibold text-[#333] mb-1.5">Breed *</label>
                <select
                  value={breed}
                  onChange={(e) => setBreed(e.target.value)}
                  className="w-full rounded-md border border-gray-300 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#0FA49C] focus:border-transparent"
                  data-testid="dog-breed"
                >
                  <option value="">Select breed...</option>
                  {BREEDS.map(b => <option key={b} value={b}>{b}</option>)}
                </select>
              </div>

              {/* Age */}
              <div>
                <label className="block text-sm font-semibold text-[#333] mb-1.5">Age *</label>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                  {AGE_OPTIONS.map(opt => (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => setAge(opt.value)}
                      className={`px-3 py-2 rounded-xl text-sm font-medium border-2 transition-colors ${
                        age === opt.value
                          ? 'border-[#0FA49C] bg-[#0FA49C]/5 text-[#0FA49C]'
                          : 'border-gray-200 text-[#666] hover:border-gray-300'
                      }`}
                      data-testid={`age-${opt.value}`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Weight */}
              <div>
                <label className="block text-sm font-semibold text-[#333] mb-1.5">Weight <span className="text-[#bbb] font-normal">(optional)</span></label>
                <div className="relative">
                  <Input
                    type="text"
                    value={weight}
                    onChange={(e) => setWeight(e.target.value)}
                    placeholder="e.g. 12kg"
                    data-testid="dog-weight"
                  />
                </div>
              </div>

              {/* Activity Level */}
              <div>
                <label className="block text-sm font-semibold text-[#333] mb-1.5">Activity Level <span className="text-[#bbb] font-normal">(optional)</span></label>
                <div className="grid grid-cols-3 gap-2">
                  {ACTIVITY_OPTIONS.map(opt => (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => setActivityLevel(opt.value)}
                      className={`px-3 py-3 rounded-xl text-center border-2 transition-colors ${
                        activityLevel === opt.value
                          ? 'border-[#0FA49C] bg-[#0FA49C]/5'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                      data-testid={`activity-${opt.value}`}
                    >
                      <p className={`text-sm font-semibold ${activityLevel === opt.value ? 'text-[#0FA49C]' : 'text-[#333]'}`}>{opt.label}</p>
                      <p className="text-xs text-[#999] mt-0.5">{opt.desc}</p>
                    </button>
                  ))}
                </div>
              </div>

              {/* Neutered */}
              <div>
                <label className="block text-sm font-semibold text-[#333] mb-1.5">Neutered/Spayed? <span className="text-[#bbb] font-normal">(optional)</span></label>
                <div className="flex gap-2">
                  {[{ v: true, l: 'Yes' }, { v: false, l: 'No' }].map(opt => (
                    <button
                      key={String(opt.v)}
                      type="button"
                      onClick={() => setNeutered(opt.v)}
                      className={`px-6 py-2 rounded-xl text-sm font-medium border-2 transition-colors ${
                        neutered === opt.v
                          ? 'border-[#0FA49C] bg-[#0FA49C]/5 text-[#0FA49C]'
                          : 'border-gray-200 text-[#666] hover:border-gray-300'
                      }`}
                      data-testid={`neutered-${opt.l.toLowerCase()}`}
                    >
                      {opt.l}
                    </button>
                  ))}
                </div>
              </div>

              {/* Sensitivities */}
              <div>
                <label className="block text-sm font-semibold text-[#333] mb-1.5">Sensitivities or Allergies <span className="text-[#bbb] font-normal">(optional)</span></label>
                <Input
                  value={sensitivities}
                  onChange={(e) => setSensitivities(e.target.value)}
                  placeholder="e.g. chicken, grain, sensitive stomach"
                  data-testid="dog-sensitivities"
                />
              </div>

              {/* Current Food */}
              <div>
                <label className="block text-sm font-semibold text-[#333] mb-1.5">Current Food <span className="text-[#bbb] font-normal">(optional)</span></label>
                <Input
                  value={currentFood}
                  onChange={(e) => setCurrentFood(e.target.value)}
                  placeholder="e.g. Royal Canin Medium Adult"
                  data-testid="dog-current-food"
                />
              </div>

              {/* Preferences */}
              <div>
                <label className="block text-sm font-semibold text-[#333] mb-1.5">Any Preferences? <span className="text-[#bbb] font-normal">(optional)</span></label>
                <Input
                  value={preferences}
                  onChange={(e) => setPreferences(e.target.value)}
                  placeholder="e.g. fish-based, grain-free, hypoallergenic"
                  data-testid="dog-preferences"
                />
              </div>

              {/* Submit */}
              <Button
                onClick={handleFindClick}
                disabled={!canSubmit || matching}
                className="w-full bg-[#0FA49C] hover:bg-[#0d8f88] text-white rounded-xl py-5 font-bold text-base"
                data-testid="find-matches-btn"
              >
                Find {dogName || 'My Dog'}'s Perfect Food
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </div>
          </motion.div>
        )}

        {/* STEP 2: Matching (loading) */}
        {step === 'matching' && (
          <motion.div
            key="matching"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="text-center py-16"
          >
            <div className="w-20 h-20 rounded-full bg-[#0FA49C]/10 flex items-center justify-center mx-auto mb-6">
              <Loader2 className="w-10 h-10 text-[#0FA49C] animate-spin" />
            </div>
            <h3 className="text-xl font-bold text-[#222] mb-2">Finding the perfect food for {dogName}...</h3>
            <p className="text-sm text-[#666]">Ted AI is analysing {dogName}'s profile and matching against our range</p>
          </motion.div>
        )}

        {/* STEP 3: Results */}
        {step === 'results' && matches && (
          <motion.div
            key="results"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
          >
            <div className="text-center mb-8">
              <CheckCircle2 className="w-12 h-12 text-[#0FA49C] mx-auto mb-3" />
              <h2 className="text-2xl font-extrabold text-[#222] mb-2">
                {dogName}'s Top Matches
              </h2>
              {summary && <p className="text-sm text-[#666] max-w-lg mx-auto">{summary}</p>}
            </div>

            <div className="space-y-4">
              {matches.map((match, i) => (
                <motion.div
                  key={match.product_id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.15 }}
                  className="bg-white rounded-2xl border border-gray-200 overflow-hidden"
                  data-testid={`match-card-${match.product_id}`}
                >
                  <div className="p-5">
                    {/* Header */}
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center gap-3">
                        {match.product?.image_url && (
                          <img src={match.product.image_url} alt="" className="w-16 h-16 rounded-xl object-contain bg-gray-50 p-1" />
                        )}
                        <div>
                          <h3 className="font-bold text-[#222] text-base">{match.product?.name || match.product_id}</h3>
                          <p className="text-xs text-[#999]">
                            {match.product?.type?.replace(/_/g, ' ')} · {match.product?.lifestage} · {match.product?.breed_size === 'all' ? 'All breeds' : match.product?.breed_size}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="text-right mr-2">
                          <span className="text-2xl font-extrabold text-[#0FA49C]">{match.match_score}%</span>
                          <p className="text-[10px] text-[#999] uppercase font-semibold">match</p>
                        </div>
                        <Button
                          size="sm"
                          onClick={() => handleAddToBox(match.product)}
                          disabled={isFull || isProductSelected(match.product?.id)}
                          className={`rounded-xl ${
                            isProductSelected(match.product?.id)
                              ? 'bg-green-100 text-green-700'
                              : 'bg-[#0FA49C] hover:bg-[#0d8f88] text-white'
                          }`}
                          data-testid={`add-match-${match.product_id}`}
                        >
                          {isProductSelected(match.product?.id) ? (
                            <><CheckCircle2 className="w-4 h-4 mr-1" /> Added</>
                          ) : (
                            <><Plus className="w-4 h-4 mr-1" /> Add to Box</>
                          )}
                        </Button>
                      </div>
                    </div>

                    {/* Benefit Icons */}
                    {match.product && (
                      <div className="flex flex-wrap gap-2 mb-4">
                        {match.product.type === 'grain_free' && (
                          <div className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-emerald-50 border border-emerald-200">
                            <svg className="w-4 h-4 text-emerald-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 2L2 7l10 5 10-5-10-5z"/><path d="M2 17l10 5 10-5"/><path d="M2 12l10 5 10-5"/></svg>
                            <span className="text-[11px] font-semibold text-emerald-700">Grain Free</span>
                          </div>
                        )}
                        {match.product.type === 'hydrolysed' && (
                          <div className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-violet-50 border border-violet-200">
                            <svg className="w-4 h-4 text-violet-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M20.42 4.58a5.4 5.4 0 0 0-7.65 0l-.77.78-.77-.78a5.4 5.4 0 0 0-7.65 0C1.46 6.7 1.33 10.28 4 13l8 8 8-8c2.67-2.72 2.54-6.3.42-8.42z"/></svg>
                            <span className="text-[11px] font-semibold text-violet-700">Hydrolysed</span>
                          </div>
                        )}
                        <div className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-[#0FA49C]/5 border border-[#0FA49C]/20">
                          <svg className="w-4 h-4 text-[#0FA49C]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M6.13 1L6 16a2 2 0 0 0 2 2h15"/><path d="M1 6.13L16 6a2 2 0 0 1 2 2v15"/></svg>
                          <span className="text-[11px] font-semibold text-[#0FA49C]">Freshly Prepared</span>
                        </div>
                        <div className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-blue-50 border border-blue-200">
                          <svg className="w-4 h-4 text-blue-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><path d="M8 12h8"/><path d="M12 8v8"/></svg>
                          <span className="text-[11px] font-semibold text-blue-700">Added Prebiotics</span>
                        </div>
                        {(match.product.tags || []).includes('skin_coat') && (
                          <div className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-amber-50 border border-amber-200">
                            <svg className="w-4 h-4 text-amber-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 3a6 6 0 0 0 9 9 9 9 0 1 1-9-9Z"/></svg>
                            <span className="text-[11px] font-semibold text-amber-700">Skin & Coat</span>
                          </div>
                        )}
                        {(match.product.tags || []).includes('joint_care_pack') && (
                          <div className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-orange-50 border border-orange-200">
                            <svg className="w-4 h-4 text-orange-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 20V6a2 2 0 0 0-2-2H8a2 2 0 0 0-2 2v14"/><path d="M2 20h20"/><path d="M14 12H10"/></svg>
                            <span className="text-[11px] font-semibold text-orange-700">Joint Support</span>
                          </div>
                        )}
                        {(match.product.tags || []).includes('fish_based') && (
                          <div className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-cyan-50 border border-cyan-200">
                            <svg className="w-4 h-4 text-cyan-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M6.5 12c.94-3.46 4.94-6 8.5-6 3.56 0 6.06 2.54 7 6-.94 3.46-3.44 6-7 6-3.56 0-7.56-2.54-8.5-6Z"/><path d="M2 12s1-2 3-2 3 2 3 2"/></svg>
                            <span className="text-[11px] font-semibold text-cyan-700">Omega-3 Rich</span>
                          </div>
                        )}
                        {(match.product.tags || []).includes('digestive_support') && (
                          <div className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-lime-50 border border-lime-200">
                            <svg className="w-4 h-4 text-lime-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
                            <span className="text-[11px] font-semibold text-lime-700">Digestive Care</span>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Why Selected */}
                    {match.why_selected && (
                      <div className="bg-[#0FA49C]/5 border border-[#0FA49C]/20 rounded-xl p-3 mb-2">
                        <p className="text-xs font-bold text-[#0FA49C] uppercase tracking-wide mb-1">Why This Suits {dogName}</p>
                        <p className="text-sm text-[#444] leading-relaxed">{match.why_selected}</p>
                      </div>
                    )}

                    {/* Key Benefits */}
                    {match.key_benefits && (
                      <div className="bg-green-50 border border-green-200 rounded-xl p-3 mb-2">
                        <p className="text-xs font-bold text-green-700 uppercase tracking-wide mb-1">Key Benefits</p>
                        <p className="text-sm text-[#444] leading-relaxed">{match.key_benefits}</p>
                      </div>
                    )}

                    {/* Feeding Tip */}
                    {match.feeding_tip && (
                      <div className="bg-amber-50 border border-amber-200 rounded-xl p-3">
                        <p className="text-xs font-bold text-amber-700 uppercase tracking-wide mb-1">Feeding Tip for {dogName}</p>
                        <p className="text-sm text-[#444] leading-relaxed">{match.feeding_tip}</p>
                      </div>
                    )}
                  </div>
                </motion.div>
              ))}
            </div>

            {/* Complete Order Prompt */}
            {selectedProducts.length > 0 && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 }}
                className={`mt-6 rounded-2xl p-5 text-center ${
                  isFull
                    ? 'bg-gradient-to-r from-[#0FA49C] to-[#0d8f88] text-white'
                    : 'bg-[#0FA49C]/5 border-2 border-[#0FA49C]/20'
                }`}
                data-testid="complete-order-prompt"
              >
                {isFull ? (
                  <>
                    <CheckCircle2 className="w-8 h-8 mx-auto mb-2 text-white" />
                    <h4 className="font-bold text-lg mb-1">Your box is full!</h4>
                    <p className="text-sm text-white/90 mb-4">
                      {selectedProducts.length} of {boxSize} samples selected. Ready to complete your order?
                    </p>
                    <Button
                      onClick={onCompleteOrder}
                      className="bg-white text-[#0FA49C] hover:bg-white/90 rounded-xl px-8 py-5 font-bold text-base"
                      data-testid="complete-order-btn"
                    >
                      Complete Your Order
                      <ArrowRight className="w-5 h-5 ml-2" />
                    </Button>
                  </>
                ) : (
                  <>
                    <p className="font-semibold text-[#222] mb-1">
                      {selectedProducts.length} of {boxSize} samples added
                    </p>
                    <p className="text-sm text-[#666]">
                      Add {boxSize - selectedProducts.length} more to complete your box.
                    </p>
                  </>
                )}
              </motion.div>
            )}

            {/* Actions */}
            <div className="flex justify-center gap-3 mt-6">
              <Button
                variant="outline"
                onClick={handleReset}
                className="rounded-xl px-6"
                data-testid="try-again-btn"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Try Another Dog
              </Button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Box Full Popup */}
      <AnimatePresence>
        {showBoxFullPopup && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
            onClick={() => setShowBoxFullPopup(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white rounded-2xl shadow-xl w-full max-w-sm overflow-hidden text-center"
              onClick={(e) => e.stopPropagation()}
              data-testid="box-full-popup"
            >
              <div className="bg-gradient-to-r from-[#0FA49C] to-[#0d8f88] p-6 text-white">
                <CheckCircle2 className="w-12 h-12 mx-auto mb-3" />
                <h3 className="font-bold text-xl">Your Box is Full!</h3>
                <p className="text-sm text-white/80 mt-1">{boxSize} samples selected and ready to go</p>
              </div>
              <div className="p-5 space-y-3">
                <Button
                  onClick={() => { setShowBoxFullPopup(false); onCompleteOrder(); }}
                  className="w-full bg-[#0FA49C] hover:bg-[#0d8f88] text-white rounded-xl py-5 font-bold text-base"
                  data-testid="go-to-checkout-btn"
                >
                  Complete Your Order
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
                <button
                  onClick={() => setShowBoxFullPopup(false)}
                  className="text-sm text-[#999] hover:text-[#666]"
                >
                  Continue browsing
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Auth Popup */}
      <AnimatePresence>
        {showAuthPopup && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
            onClick={() => setShowAuthPopup(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-2xl shadow-xl w-full max-w-sm overflow-hidden"
              onClick={(e) => e.stopPropagation()}
              data-testid="dog-match-auth-popup"
            >
              <div className="bg-purple-600 p-5 text-white flex items-start justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-white/20 flex items-center justify-center">
                    <PawPrint className="w-5 h-5" />
                  </div>
                  <div>
                    <h3 className="font-bold text-base">Sign in to continue</h3>
                    <p className="text-xs text-white/80">Create an account to get {dogName}'s results</p>
                  </div>
                </div>
                <button onClick={() => setShowAuthPopup(false)} className="p-1 hover:bg-white/20 rounded-lg">
                  <X className="w-4 h-4" />
                </button>
              </div>

              <div className="p-5 space-y-4">
                {/* Google */}
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleGoogleLogin}
                  className="w-full py-5 font-semibold text-sm border-gray-300 hover:bg-gray-50"
                  data-testid="dog-match-google-btn"
                >
                  <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
                    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                  </svg>
                  Continue with Google
                </Button>

                <div className="flex items-center gap-3">
                  <div className="flex-1 h-px bg-gray-200" />
                  <span className="text-xs text-[#999] font-medium">or {authMode === 'login' ? 'sign in' : 'create account'}</span>
                  <div className="flex-1 h-px bg-gray-200" />
                </div>

                <form onSubmit={handleAuthSubmit} className="space-y-3">
                  {authMode === 'register' && (
                    <div className="relative">
                      <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <Input
                        value={authName}
                        onChange={(e) => setAuthName(e.target.value)}
                        placeholder="Your name"
                        className="pl-10"
                        data-testid="dog-match-auth-name"
                      />
                    </div>
                  )}
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input
                      type="email"
                      value={authEmail}
                      onChange={(e) => setAuthEmail(e.target.value)}
                      placeholder="Email address"
                      required
                      className="pl-10"
                      data-testid="dog-match-auth-email"
                    />
                  </div>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input
                      type="password"
                      value={authPassword}
                      onChange={(e) => setAuthPassword(e.target.value)}
                      placeholder="Password"
                      required
                      minLength={6}
                      className="pl-10"
                      data-testid="dog-match-auth-password"
                    />
                  </div>

                  {authError && (
                    <p className="text-sm text-red-600 bg-red-50 rounded-lg px-3 py-2">{authError}</p>
                  )}

                  <Button
                    type="submit"
                    disabled={authSubmitting}
                    className="w-full bg-purple-600 hover:bg-purple-700 text-white rounded-xl py-5 font-bold"
                    data-testid="dog-match-auth-submit"
                  >
                    {authSubmitting ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <>
                        {authMode === 'login' ? 'Sign In & Find Matches' : 'Create Account & Find Matches'}
                        <ArrowRight className="w-4 h-4 ml-2" />
                      </>
                    )}
                  </Button>
                </form>

                <p className="text-center text-xs text-[#666]">
                  {authMode === 'login' ? (
                    <>No account? <button onClick={() => { setAuthMode('register'); setAuthError(''); }} className="text-purple-600 font-semibold hover:underline">Sign up</button></>
                  ) : (
                    <>Have an account? <button onClick={() => { setAuthMode('login'); setAuthError(''); }} className="text-purple-600 font-semibold hover:underline">Sign in</button></>
                  )}
                </p>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
